import React from "react";
import Card from "components/card";
import { SODStatsCard } from "components/sod";
import {
  CrawlerBannerPieChart,
  CrawlerPromotePieChart,
  CrawlerTypeDistChart,
  CrawlerActivityChart,
  CrawlerTopRetailersChart,
  CrawlerImageStatsChart,
} from "components/crawler";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";
import "@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css";
import "react-calendar/dist/Calendar.css";
import {
  MdStorefront,
  MdCheckCircle,
  MdCancel,
  MdCampaign,
  MdVolumeOff,
  MdDiamond,
  MdFavorite,
  MdImage,
  MdPeople,
  MdSearch,
  MdFilterList,
  MdOpenInNew,
  MdChevronLeft,
  MdChevronRight,
  MdRestartAlt,
} from "react-icons/md";

const formatDateTime = (date) => {
  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} 00:00:00`;
};

const buildWebsiteUrl = (retailerWebsite) => {
  if (!retailerWebsite) return "";
  if (retailerWebsite.includes(".")) {
    return retailerWebsite.startsWith("http") ? retailerWebsite : `https://${retailerWebsite}`;
  }
  return `https://${retailerWebsite}.com`;
};

const BooleanBadge = ({ value }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      value
        ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400"
    }`}
  >
    {value ? <MdCheckCircle className="h-3 w-3" /> : <MdCancel className="h-3 w-3" />}
    {value ? "Yes" : "No"}
  </span>
);

const TypeBadge = ({ active, label }) => (
  <span
    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      active
        ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
        : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
    }`}
  >
    {label}
  </span>
);

export default function CrawlerAnalytics() {
  const today = new Date();
  const twoWeeksAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);

  const [fromdate, setFromdate] = React.useState(formatDateTime(twoWeeksAgo));
  const [todate, setTodate] = React.useState(formatDateTime(today));
  const [dateRange, setDateRange] = React.useState([twoWeeksAgo, today]);
  const [retailerNo, setRetailerNo] = React.useState("");
  const [id, setId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = 30;
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalImages, setModalImages] = React.useState([]);

  const computeFlags = (row) => {
    const typeText = (row?.type || "").toString().toLowerCase();
    const descText = (row?.imageDesc || "").toString().toLowerCase();
    return {
      hasFashion: typeText.includes("fashion") || descText.includes("fashion"),
      hasBridal: typeText.includes("bridal") || descText.includes("bridal"),
    };
  };

  const parseRunDate = (value) => {
    if (!value) return null;
    const d = new Date(String(value).replace(/-/g, "/"));
    return isNaN(d) ? null : d;
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("query", "allCrawls");
      if (fromdate) params.set("fromdate", fromdate);
      if (todate) params.set("todate", todate);
      if (retailerNo) params.set("retailerNo", retailerNo);
      if (id) params.set("id", id);

      const resp = await fetch(
        `https://api.gabrielny.com:8080/ai_dashboard.php?${params.toString()}`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      const array = Array.isArray(json) ? json.slice() : [];
      array.sort((a, b) => {
        const da = parseRunDate(a?.runDate)?.getTime() ?? 0;
        const db = parseRunDate(b?.runDate)?.getTime() ?? 0;
        return db - da;
      });
      setRows(array);
    } catch (e) {
      setError(e.message || "Failed to fetch data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchData(); }, [fromdate, todate]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (e) => {
    e.preventDefault();
    setPage(0);
    fetchData();
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    if (Array.isArray(range) && range[0] && range[1]) {
      const start = new Date(range[0]);
      const end = new Date(range[1]);
      const startAtMidnight = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0);
      const endAtEndOfDay = new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59);
      setFromdate(formatDateTime(startAtMidnight));
      setTodate(formatDateTime(endAtEndOfDay));
      setPage(0);
    }
  };

  const [websiteQuery, setWebsiteQuery] = React.useState("");
  const [bannerFilter, setBannerFilter] = React.useState("all");
  const [promoteFilter, setPromoteFilter] = React.useState("all");
  const [typeFilter, setTypeFilter] = React.useState("all");

  const filteredRows = React.useMemo(() => {
    const norm = (s) => String(s || "").toLowerCase();
    return rows.filter((r) => {
      const { hasFashion, hasBridal } = computeFlags(r || {});
      if (websiteQuery) {
        const site = buildWebsiteUrl(r?.retailerWebsite || "");
        if (!norm(site).includes(norm(websiteQuery))) return false;
      }
      if (bannerFilter !== "all") {
        const val = String(r?.gabcoBanner) === "1";
        if ((bannerFilter === "true" && !val) || (bannerFilter === "false" && val)) return false;
      }
      if (promoteFilter !== "all") {
        const val = String(r?.isPromote) === "1";
        if ((promoteFilter === "true" && !val) || (promoteFilter === "false" && val)) return false;
      }
      if (typeFilter !== "all") {
        if (typeFilter === "fashion" && !hasFashion) return false;
        if (typeFilter === "bridal" && !hasBridal) return false;
        if (typeFilter === "both" && !(hasFashion && hasBridal)) return false;
        if (typeFilter === "neither" && (hasFashion || hasBridal)) return false;
      }
      return true;
    });
  }, [rows, websiteQuery, bannerFilter, promoteFilter, typeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = React.useMemo(() => {
    let bannerTrue = 0, promoteTrue = 0, fashionCount = 0, bridalCount = 0, withImages = 0;
    const uniqueRetailers = new Set();
    let latestRun = null;

    filteredRows.forEach((row) => {
      if (String(row?.gabcoBanner) === "1") bannerTrue++;
      if (String(row?.isPromote) === "1") promoteTrue++;
      const { hasFashion, hasBridal } = computeFlags(row);
      if (hasFashion) fashionCount++;
      if (hasBridal) bridalCount++;
      if (row?.imageURL) withImages++;
      if (row?.retailerNo) uniqueRetailers.add(row.retailerNo);
      const d = parseRunDate(row?.runDate);
      if (d && (!latestRun || d > latestRun)) latestRun = d;
    });

    const total = filteredRows.length;
    return {
      total,
      bannerTrue,
      promoteTrue,
      fashionCount,
      bridalCount,
      bannerFalse: total - bannerTrue,
      promoteFalse: total - promoteTrue,
      withImages,
      uniqueRetailers: uniqueRetailers.size,
      latestRun: latestRun ? latestRun.toISOString().slice(0, 19).replace("T", " ") : "N/A",
    };
  }, [filteredRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = React.useMemo(
    () => filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filteredRows, page]
  );

  React.useEffect(() => {
    if (page > totalPages - 1) setPage(0);
  }, [totalPages, page]);

  const resetFilters = () => {
    setFromdate(formatDateTime(twoWeeksAgo));
    setTodate(formatDateTime(today));
    setDateRange([twoWeeksAgo, today]);
    setRetailerNo("");
    setId("");
    setWebsiteQuery("");
    setBannerFilter("all");
    setPromoteFilter("all");
    setTypeFilter("all");
    setPage(0);
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 py-32">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 font-dm">
          Loading Crawler Analytics...
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 font-dm">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white font-dm">
            Crawler Analytics
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 font-dm">
            Retailer website crawl insights &bull; Last run: {stats.latestRun}
          </p>
        </div>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            Refreshing...
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <SODStatsCard
          title="Retailers Scanned"
          value={stats.total}
          icon={<MdStorefront className="h-6 w-6 text-indigo-500" />}
          cardClassName="border border-gray-100 dark:border-navy-600"
        />
        <SODStatsCard
          title="Banners Found"
          value={stats.bannerTrue}
          subtitle={stats.total ? `${Math.round((stats.bannerTrue / stats.total) * 100)}% of sites` : ""}
          icon={<MdCheckCircle className="h-6 w-6 text-emerald-500" />}
          cardClassName="border border-gray-100 dark:border-navy-600"
        />
        <SODStatsCard
          title="Promoted"
          value={stats.promoteTrue}
          subtitle={stats.total ? `${Math.round((stats.promoteTrue / stats.total) * 100)}% of sites` : ""}
          icon={<MdCampaign className="h-6 w-6 text-violet-500" />}
          cardClassName="border border-gray-100 dark:border-navy-600"
        />
        <SODStatsCard
          title="With Images"
          value={stats.withImages || 0}
          subtitle={stats.total ? `${Math.round((stats.withImages / stats.total) * 100)}% coverage` : ""}
          icon={<MdImage className="h-6 w-6 text-amber-500" />}
          cardClassName="border border-gray-100 dark:border-navy-600"
        />
        <SODStatsCard
          title="Unique Retailers"
          value={stats.uniqueRetailers || 0}
          icon={<MdPeople className="h-6 w-6 text-blue-500" />}
          cardClassName="border border-gray-100 dark:border-navy-600"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-50 dark:bg-pink-900/20">
            <MdFavorite className="h-5 w-5 text-pink-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Fashion</p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">{stats.fashionCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 dark:bg-purple-900/20">
            <MdDiamond className="h-5 w-5 text-purple-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Bridal</p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">{stats.bridalCount}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/20">
            <MdCancel className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">No Banner</p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">{stats.bannerFalse}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white p-4 dark:border-navy-600 dark:bg-navy-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/20">
            <MdVolumeOff className="h-5 w-5 text-orange-500" />
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Not Promoted</p>
            <p className="text-lg font-bold text-navy-700 dark:text-white">{stats.promoteFalse}</p>
          </div>
        </div>
      </div>

      {/* Charts Row 1 - Activity + Top Retailers */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <CrawlerActivityChart rows={filteredRows} />
        <CrawlerTopRetailersChart rows={filteredRows} limit={10} />
      </div>

      {/* Charts Row 2 - Pie/Donut Charts */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        <CrawlerBannerPieChart bannerTrue={stats.bannerTrue} bannerFalse={stats.bannerFalse} />
        <CrawlerPromotePieChart promoteTrue={stats.promoteTrue} promoteFalse={stats.promoteFalse} />
        <CrawlerImageStatsChart rows={filteredRows} />
      </div>

      {/* Charts Row 3 - Type Distribution (full width) */}
      <CrawlerTypeDistChart rows={filteredRows} />

      {/* Filters */}
      <Card extra="p-5">
        <div className="mb-4 flex items-center gap-2">
          <MdFilterList className="h-5 w-5 text-gray-500" />
          <h3 className="text-base font-bold text-navy-700 dark:text-white font-dm">
            Filter &amp; Search
          </h3>
          <span className="ml-auto text-xs text-gray-400 font-dm">
            {filteredRows.length} of {rows.length} results
          </span>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Date Range</label>
            <DateRangePicker
              clearIcon={null}
              onChange={handleDateRangeChange}
              value={dateRange}
              calendarIcon={null}
              className="w-full"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Retailer No</label>
            <input
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
              value={retailerNo}
              onChange={(e) => setRetailerNo(e.target.value)}
              placeholder="e.g., C05632"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Record ID</label>
            <input
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="Record ID"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Website contains</label>
            <div className="relative">
              <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
                value={websiteQuery}
                onChange={(e) => { setWebsiteQuery(e.target.value); setPage(0); }}
                placeholder="domain or url..."
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Category</label>
            <select
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <option value="all">All Categories</option>
              <option value="fashion">Fashion</option>
              <option value="bridal">Bridal</option>
              <option value="both">Fashion &amp; Bridal</option>
              <option value="neither">Neither</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Banner Status</label>
            <select
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
              value={bannerFilter}
              onChange={(e) => { setBannerFilter(e.target.value); setPage(0); }}
            >
              <option value="all">All</option>
              <option value="true">Has Banner</option>
              <option value="false">No Banner</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400">Promotion</label>
            <select
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:ring-indigo-900"
              value={promoteFilter}
              onChange={(e) => { setPromoteFilter(e.target.value); setPage(0); }}
            >
              <option value="all">All</option>
              <option value="true">Promoted</option>
              <option value="false">Not Promoted</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 active:bg-indigo-800"
            >
              <MdSearch className="h-4 w-4" />
              Search
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              <MdRestartAlt className="h-4 w-4" />
              Reset
            </button>
          </div>
        </form>
      </Card>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900 dark:bg-red-900/20">
          <MdCancel className="h-5 w-5 text-red-500" />
          <p className="text-sm text-red-700 dark:text-red-400 font-dm">{error}</p>
        </div>
      )}

      {/* Data Table */}
      <Card extra="overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 dark:border-navy-600">
          <h3 className="text-base font-bold text-navy-700 dark:text-white font-dm">
            Crawl Results
          </h3>
          <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
            {filteredRows.length} records
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80 dark:border-navy-600 dark:bg-navy-700/50">
                {["C0 Number", "Name", "Website", "RR", "Banner", "Image Description", "Fashion", "Bridal", "Promote", "Images", "Run Date"].map(
                  (h) => (
                    <th
                      key={h}
                      className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-navy-600">
              {filteredRows.length === 0 && !loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-sm text-gray-400 dark:text-gray-500">
                    No records match your filters
                  </td>
                </tr>
              ) : (
                pagedRows.map((row, idx) => {
                  const { hasFashion, hasBridal } = computeFlags(row || {});
                  const website = buildWebsiteUrl(row?.retailerWebsite || "");
                  return (
                    <tr
                      key={row?.id || idx}
                      className="transition-colors hover:bg-indigo-50/40 dark:hover:bg-navy-700/40"
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-medium text-navy-700 dark:text-white">
                        {row?.retailerNo || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                        {row?.retailerName || "N/A"}
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3">
                        {website ? (
                          <a
                            href={website}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 hover:underline dark:text-indigo-400"
                          >
                            {website.replace(/^https?:\/\//, "").slice(0, 30)}
                            <MdOpenInNew className="h-3 w-3 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {row?.retailRelationsRep || "N/A"}
                      </td>
                      <td className="px-4 py-3">
                        <BooleanBadge value={String(row?.gabcoBanner) === "1"} />
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-gray-600 dark:text-gray-300" title={row?.imageDesc || ""}>
                        {row?.imageDesc || "N/A"}
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge active={hasFashion} label={hasFashion ? "Yes" : "No"} />
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge active={hasBridal} label={hasBridal ? "Yes" : "No"} />
                      </td>
                      <td className="px-4 py-3">
                        <BooleanBadge value={String(row?.isPromote) === "1"} />
                      </td>
                      <td className="px-4 py-3">
                        {row?.imageURL ? (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
                            onClick={() => {
                              const urls = String(row.imageURL || "")
                                .split(",")
                                .map((u) => u.trim())
                                .filter((u) => u);
                              setModalImages(urls);
                              setModalOpen(true);
                            }}
                          >
                            <MdImage className="h-3.5 w-3.5" />
                            {String(row.imageURL).split(",").filter(Boolean).length}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">None</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {row?.runDate || "N/A"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3 dark:border-navy-600">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {filteredRows.length === 0 ? 0 : page * PAGE_SIZE + 1}&ndash;{Math.min((page + 1) * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-navy-600"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <MdChevronLeft className="h-5 w-5" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i;
              } else if (page < 3) {
                pageNum = i;
              } else if (page > totalPages - 4) {
                pageNum = totalPages - 5 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-semibold transition-colors ${
                    pageNum === page
                      ? "bg-indigo-600 text-white"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-600"
                  }`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            <button
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:text-gray-400 dark:hover:bg-navy-600"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <MdChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </Card>

      {/* Image Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setModalOpen(false)}
        >
          {(() => {
            const count = modalImages.length;
            const maxW =
              count <= 1 ? "max-w-3xl" : count <= 2 ? "max-w-5xl" : "max-w-6xl";
            const gridCols =
              count <= 1
                ? "grid-cols-1"
                : count === 2
                ? "grid-cols-2"
                : "grid-cols-2 md:grid-cols-3";
            const imgClass =
              count <= 1
                ? "h-auto max-h-[70vh] w-full object-contain"
                : "h-56 w-full object-cover";
            return (
              <div
                className={`max-h-[90vh] w-full ${maxW} overflow-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-navy-800`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-navy-700 dark:text-white font-dm">
                    Images ({count})
                  </h2>
                  <button
                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-navy-600 dark:text-gray-300"
                    onClick={() => setModalOpen(false)}
                  >
                    &times;
                  </button>
                </div>
                {modalImages.length === 0 ? (
                  <p className="text-sm text-gray-500">No images</p>
                ) : (
                  <div className={`grid ${gridCols} gap-4`}>
                    {modalImages.map((src, i) => (
                      <a
                        href={src}
                        key={i}
                        target="_blank"
                        rel="noreferrer"
                        className="group block overflow-hidden rounded-xl border border-gray-200 transition-shadow hover:shadow-lg dark:border-navy-600"
                      >
                        <img
                          src={src}
                          alt={`Image ${i + 1}`}
                          className={`${imgClass} transition-transform group-hover:scale-[1.02]`}
                        />
                        <div className="p-2 text-xs text-gray-500 dark:text-gray-400 truncate">
                          {src}
                        </div>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}
